import React, { useState } from 'react';
import { useJWTAuth } from '../../auth/JWTAuthContext';
import { useJWTSync } from '../../auth/useJWTSync';
import { useToast } from '../../contexts/ToastContext';

const StrategySliders = ({ hero, onStrategyUpdate }) => {
  const { user, getAuthHeaders, isAuthenticated } = useJWTAuth();
  const { jwtUser } = useJWTSync(); // Sync JWT with entitlements
  const { success, error } = useToast();
  const [strategies, setStrategies] = useState({
    strategy_aggression: hero.strategy_aggression ?? 50,
    strategy_looting: hero.strategy_looting ?? 50,
    strategy_positioning: hero.strategy_positioning ?? 50,
    strategy_melee_ranged: hero.strategy_melee_ranged ?? 50,
    strategy_item_use: hero.strategy_item_use ?? 50
  });

  const [loading, setLoading] = useState(false);
  
  const isOwner = isAuthenticated() && user && hero.owner_id === user.id;
  
  // Debug logging
  console.log('🔍 [StrategySliders] Owner check:', {
    isAuthenticated: isAuthenticated(),
    user: user,
    userId: user?.id,
    warriorOwnerId: hero.owner_id,
    isOwner: isOwner
  });

  const strategyConfigs = [
    {
      key: 'strategy_aggression',
      name: 'Combat Style',
      description: 'How aggressively this hero fights',
      lowLabel: 'Defensive',
      highLabel: 'Aggressive',
      color: 'red',
      icon: '⚔️'
    },
    {
      key: 'strategy_looting',
      name: 'Resource Priority',
      description: 'Whether to prioritize food or combat',
      lowLabel: 'Heal First',
      highLabel: 'Loot First',
      color: 'green',
      icon: '🎒'
    },
    {
      key: 'strategy_positioning',
      name: 'Battle Position',
      description: 'Where to position during battle',
      lowLabel: 'Center Charge',
      highLabel: 'Edge Control',
      color: 'blue',
      icon: '🎯'
    },
    {
      key: 'strategy_melee_ranged',
      name: 'Combat Range',
      description: 'Preferred combat distance',
      lowLabel: 'Melee Focus',
      highLabel: 'Ranged Focus',
      color: 'purple',
      icon: '🏹'
    },
    {
      key: 'strategy_item_use',
      name: 'Item Usage',
      description: 'How frequently to use consumable items',
      lowLabel: 'Conservative',
      highLabel: 'Liberal',
      color: 'yellow',
      icon: '🧪'
    }
  ];

  const getColorClass = (color) => {
    const colors = {
      red: 'text-red-400',
      green: 'text-green-400',
      blue: 'text-blue-400',
      purple: 'text-purple-400',
      yellow: 'text-yellow-400'
    };
    return colors[color] || 'text-gray-400';
  };

  const getPillColorClass = (color) => {
    const colors = {
      red: 'bg-red-500/20 border-red-500/30 text-red-400',
      green: 'bg-green-500/20 border-green-500/30 text-green-400',
      blue: 'bg-blue-500/20 border-blue-500/30 text-blue-400',
      purple: 'bg-purple-500/20 border-purple-500/30 text-purple-400',
      yellow: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400'
    };
    return colors[color] || 'bg-gray-500/20 border-gray-500/30 text-gray-400';
  };

  const getSliderColorClass = (color) => {
    const colors = {
      red: 'bg-red-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500',
      yellow: 'bg-yellow-500'
    };
    return colors[color] || 'bg-gray-500';
  };

  const handleSliderChange = (key, value) => {
    setStrategies(prev => ({
      ...prev,
      [key]: parseInt(value)
    }));
  };

  const saveStrategies = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`/api/heroes/${hero.id}/strategies`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(strategies)
      });
      
      const data = await response.json();
      
      if (data.success) {
        onStrategyUpdate(data.data);
        success('Strategy preferences updated successfully!');
      } else {
        console.error('Failed to update strategies:', data.error);
        error(`Failed to update strategies: ${data.error}`);
      }
    } catch (error) {
      console.error('Error updating strategies:', error);
      error('Error updating strategies');
    } finally {
      setLoading(false);
    }
  };

  const getStrategyDescription = (key, value) => {
    const descriptions = {
      strategy_aggression: {
        low: 'Prefers defensive positioning and avoiding direct combat',
        medium: 'Balanced approach to combat and defense',
        high: 'Aggressively pursues enemies and engages in combat'
      },
      strategy_looting: {
        low: 'Prioritizes healing and survival over collecting resources',
        medium: 'Balances resource collection with healing needs',
        high: 'Actively seeks out food and items, even in dangerous situations'
      },
      strategy_positioning: {
        low: 'Charges toward the center of the battlefield',
        medium: 'Moves around the battlefield with moderate positioning',
        high: 'Stays near edges and uses positioning to advantage'
      },
      strategy_melee_ranged: {
        low: 'Prefers close combat and melee weapons',
        medium: 'Uses both melee and ranged combat as needed',
        high: 'Favors ranged weapons and keeping distance from enemies'
      },
      strategy_item_use: {
        low: 'Uses items sparingly, only when absolutely necessary',
        medium: 'Uses items when beneficial but not excessively',
        high: 'Frequently uses items to gain advantages in combat'
      }
    };

    if (value < 30) return descriptions[key].low;
    if (value > 70) return descriptions[key].high;
    return descriptions[key].medium;
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-xl font-bold text-white mb-4">AI Strategy Preferences</h3>
      <p className="text-gray-400 text-sm mb-6">
        Configure how your Hero behaves in battle. These settings affect AI decision-making.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {strategyConfigs.map(config => {
          const value = strategies[config.key];
          const description = getStrategyDescription(config.key, value);

          return (
            <div key={config.key} className="space-y-3 bg-gray-900 border border-white/10 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm font-medium ${getPillColorClass(config.color)}`}>
                  <span className="text-lg">{config.icon}</span>
                  <span>{config.name}</span>
                </div>
                <span className="text-white font-bold">{value}</span>
              </div>
              
              <p className="text-gray-400 text-sm">{config.description}</p>
              
              <div className="space-y-2">
                <div className="flex justify-between text-sm text-gray-300">
                  <span>{config.lowLabel}</span>
                  <span>{config.highLabel}</span>
                </div>
                
                <div className="relative">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => handleSliderChange(config.key, e.target.value)}
                    className={`w-full h-2 rounded-lg appearance-none cursor-pointer slider ${getSliderColorClass(config.color)}`}
                    style={{
                      background: `linear-gradient(to right, #374151 0%, #374151 ${value}%, ${getSliderColorClass(config.color).replace('bg-', '#')} ${value}%, ${getSliderColorClass(config.color).replace('bg-', '#')} 100%)`
                    }}
                  />
                </div>
                
                <p className="text-sm text-gray-300 italic">{description}</p>
              </div>
            </div>
          );
        })}
      </div>

      {isOwner && (
        <div className="mt-6 pt-4 border-t border-gray-600 flex justify-center">
          <button
            onClick={saveStrategies}
            disabled={loading}
            className={`px-8 py-3 rounded-lg font-medium transition-colors ${
              loading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {loading ? 'Saving...' : 'Save Strategy Preferences'}
          </button>
        </div>
      )}

      <div className="mt-4 p-4 bg-gray-700 rounded-lg">
        <h4 className="text-white font-bold mb-2">Strategy Effects</h4>
        <div className="text-sm text-gray-300 space-y-1">
          <div><span className="text-red-400">Combat Style:</span> Affects engagement distance and pursuit behavior</div>
          <div><span className="text-green-400">Resource Priority:</span> Affects food seeking distance and healing thresholds</div>
          <div><span className="text-blue-400">Battle Position:</span> Affects wandering behavior and positioning preferences</div>
        </div>
      </div>
    </div>
  );
};

export default StrategySliders;
